import { t } from "@lingui/macro";
import graphql from "babel-plugin-relay/macro";
import { useAtomValue, useSetAtom } from "jotai";
import React, { Suspense, useEffect } from "react";
import { PreloadedQuery, usePreloadedQuery, useQueryLoader } from "react-relay";

import {
  DropdownSelect,
  DropdownSelectOption,
} from "@whoosh/design-system/src/components/controls/DropdownSelect";
import { Avatar } from "@whoosh/design-system/src/components/display/Avatar";
import {
  VStack,
  Text,
  Box,
  HStack,
} from "@whoosh/design-system/src/components/primitive";

import {
  currentSignUpRowAtom,
  originalSignUpRowAtom,
  mergePrimaryChargeRulePricesAtom,
  primaryChargeSelectionByGroupData,
  updatePrimaryChargeSelectionForWholeGroup,
  userTypeAtom,
} from "../../../atoms/eventSignUpProcess";
import { PerGroupSelectionQuery } from "./__generated__/PerGroupSelectionQuery.graphql";

export type PerGroupSelectionData = {
  avatarList?: {
    profileImageId?: string;
    firstName: string | null;
    lastName: string | null;
    type: "member" | "guest";
  }[];
  currentSelectedPrimaryChargeId: string | null;
};

export type PerGroupSelectionProps = {
  options: DropdownSelectOption<string>[];
};

export const PerGroupSelection = ({ options }: PerGroupSelectionProps) => {
  const currentRow = useAtomValue(currentSignUpRowAtom);
  const originalRow = useAtomValue(originalSignUpRowAtom);
  const [queryRef, loadQuery] = useQueryLoader<PerGroupSelectionQuery>(
    PerGroupSelectionQueryString
  );
  const signUpId =
    currentRow?.id &&
    currentRow.ownerClient?.id === originalRow?.ownerClient?.id
      ? currentRow.id
      : null;

  useEffect(() => {
    if (signUpId) {
      loadQuery({ signUpId }, { fetchPolicy: "store-and-network" });
    }
  }, [loadQuery, signUpId]);

  const fallback = <PerGroupChargeDropdown options={options} />;
  return signUpId && queryRef ? (
    <Suspense fallback={fallback}>
      <SavedGroupChargeOptions queryRef={queryRef} fallbackOptions={options} />
    </Suspense>
  ) : (
    fallback
  );
};

const SavedGroupChargeOptions = ({
  queryRef,
  fallbackOptions,
}: {
  queryRef: PreloadedQuery<PerGroupSelectionQuery>;
  fallbackOptions: DropdownSelectOption<string>[];
}) => {
  const data = usePreloadedQuery<PerGroupSelectionQuery>(
    PerGroupSelectionQueryString,
    queryRef
  );
  const isMemberUser = useAtomValue(userTypeAtom) === "MEMBER";
  const group = useAtomValue(primaryChargeSelectionByGroupData);
  const signUp = data.node?.__typename === "EventSignUp" ? data.node : null;
  const options = [...fallbackOptions];
  const selectedCharge = signUp?.registrations?.edges?.find(
    (edge) =>
      edge?.node?.primaryChargeType?.id === group.currentSelectedPrimaryChargeId
  )?.node?.primaryChargeType;
  const savedPrice = selectedCharge?.chargeRuleSet.chargeRules?.edges?.find(
    (edge) => edge?.node?.price != null
  )?.node?.price;
  const mergePrices = useSetAtom(mergePrimaryChargeRulePricesAtom);
  useEffect(() => {
    if (selectedCharge?.id && savedPrice != null) {
      mergePrices([{ id: selectedCharge.id, price: savedPrice }]);
    }
  }, [mergePrices, selectedCharge?.id, savedPrice]);
  if (
    selectedCharge?.chargeRuleSet.displayName &&
    !options.some((option) => option.value === selectedCharge.id)
  ) {
    options.push({
      value: selectedCharge.id,
      label: selectedCharge.chargeRuleSet.displayName,
      isDisabled: true,
      rightElement:
        savedPrice == null ? undefined : (
          <Text>{`$${(savedPrice / 100).toFixed(2)}`}</Text>
        ),
    });
  }
  const isRateLocked =
    isMemberUser &&
    !!selectedCharge &&
    !fallbackOptions.some(
      (option) => option.value === selectedCharge.id && !option.isDisabled
    );
  return <PerGroupChargeDropdown options={options} isDisabled={isRateLocked} />;
};

const PerGroupChargeDropdown = ({
  options,
  isDisabled = false,
}: PerGroupSelectionProps & { isDisabled?: boolean }) => {
  const data = useAtomValue(primaryChargeSelectionByGroupData);
  const avatarListLength = data.avatarList?.length ?? 0;
  const isMemberUser = useAtomValue(userTypeAtom) === "MEMBER";
  const updatePrimaryChargeSelection = useSetAtom(
    updatePrimaryChargeSelectionForWholeGroup
  );

  return (
    <VStack px={6} mb={6}>
      <HStack py={4} alignItems="center">
        {data.avatarList?.map((avatar, i) => {
          return (
            <Box
              key={avatar.type + i}
              position="relative"
              left={-24 * i}
              borderWidth={1.5}
              borderColor="grayscale-0"
              backgroundColor="grayscale-0"
              borderRadius="2"
              overflow="hidden"
              style={{ zIndex: avatarListLength - i, padding: 1 }}
            >
              <Box
                position={"absolute"}
                top={0}
                left={0}
                right={0}
                bottom={0}
                backgroundColor="grayscale-0"
                opacity={0.4 * i}
                zIndex={1}
              />
              <Avatar
                profileImagePublicId={avatar.profileImageId}
                firstName={avatar.firstName}
                lastName={avatar.lastName}
                type={avatar.type}
                size="sm"
              />
            </Box>
          );
        })}
        <VStack pl={4} flex={1}>
          <Text maxFontSizeMultiplier={2} fontWeight={600}>
            {isMemberUser ? t`Your Group` : t`Group`}
          </Text>
        </VStack>
      </HStack>
      <DropdownSelect
        value={data.currentSelectedPrimaryChargeId ?? ""}
        options={options}
        isDisabled={isDisabled}
        onChange={(val) => {
          if (val) updatePrimaryChargeSelection({ primaryChargeTypeId: val });
        }}
      />
    </VStack>
  );
};

const PerGroupSelectionQueryString = graphql`
  query PerGroupSelectionQuery($signUpId: ID!) {
    node(id: $signUpId) {
      __typename
      ... on EventSignUp {
        registrations(first: 50) {
          edges {
            node {
              primaryChargeType {
                id
                chargeRuleSet {
                  displayName
                  chargeRules {
                    edges {
                      node {
                        price
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;
